import { useMemo } from 'react';

import './skill-stats-card.css';
import { APP_TEXT } from '../../const/app-text';
import { ConfidencePills } from '../confidence-pills';
import { OverallConfidenceArrow } from './overall-confidence-arrow';
import { useWidth } from '../../hooks/utils/use-responsive';

const cardText = APP_TEXT.pages.skillStats.card;

export type ConfidenceValue = 1 | 2 | 3 | 4 | 5;
export const CONFIDENCE_LABELS: Record<ConfidenceValue, string> = {
  1: 'Not at all',
  2: 'Not very',
  3: 'Unsure',
  4: 'Fairly',
  5: 'Very',
};
export interface SkillStatItem {
  id: string;
  label: string;
  confidence: ConfidenceValue;
  previousConfidence: ConfidenceValue | undefined;
  ordinal: number;
}

export interface SkillStatGroup {
  id: string;
  title: string;
  description: string;
  accentColor: string;
  skills: SkillStatItem[];
  ordinal: number;
  hasResources: boolean;
}

const SkillIndexBadge = ({ badgeNumber }: { badgeNumber: number }) => {
  return (
    <span
      className="skillStatsIndexBadge"
      aria-hidden="true"
    >
      {badgeNumber}
    </span>
  );
};

const SkillStatRow = ({
  item,
  accentColor,
  ordinal,
}: {
  item: SkillStatItem;
  accentColor: string;
  ordinal: number;
}) => {
  const confidenceLabel = CONFIDENCE_LABELS[item.confidence];

  return (
    <li className="skillStatsRow">
      <div className="skillStatsRowLeft">
        <SkillIndexBadge badgeNumber={ordinal} />
        <span className="skillStatsSkillLabel">{item.label}</span>
      </div>

      <div className="skillStatsRowRight">
        <span className="skillStatsConfidenceLabel">{confidenceLabel}</span>
        <ConfidencePills
          value={item.confidence}
          color={accentColor}
        />
      </div>
    </li>
  );
};

export const SkillStatCard = ({
  group,
  onCtaClick,
  previousQuizDate,
}: {
  group: SkillStatGroup;
  onCtaClick?: (group: SkillStatGroup) => void;
  previousQuizDate?: string;
}) => {
  const overallConfidence = Math.floor(
    group.skills.reduce((sum, skill) => sum + skill.confidence, 0) /
      Math.max(group.skills.length, 1)
  ) as ConfidenceValue;

  const previousOverallConfidence = Math.floor(
    group.skills.reduce((sum, skill) => sum + (skill.previousConfidence ?? 0), 0) /
      Math.max(group.skills.length, 1)
  ) as ConfidenceValue;

  const overallLabel = CONFIDENCE_LABELS[overallConfidence];

  const trendText = useMemo(() => {
    if (overallConfidence === 5) {
      return cardText.maxConfidence;
    }
    if (previousQuizDate && overallConfidence > previousOverallConfidence) {
      return `${cardText.trendUp} ${previousQuizDate}`;
    }
    return null;
  }, [overallConfidence, previousOverallConfidence, previousQuizDate]);

  const screenSize = useWidth();

  return (
    <article className="skillStatsCard">
      <div
        className="skillStatsAccent"
        style={{ backgroundColor: group.accentColor }}
        aria-hidden="true"
      />

      <div className="skillStatsCardContent">
        <header className="skillStatsCardHeader">
          <h2 className="skillStatsCardTitle">{group.title}</h2>
          <p className="skillStatsCardDescription">{group.description}</p>
        </header>

        <hr className="skillStatsDivider" />

        <section
          className="skillStatsTable"
          aria-label={group.title}
        >
          <div className="skillStatsTableHeader">
            <span>{cardText.category}</span>
            <span>{cardText.confidence}</span>
          </div>

          <ul className="skillStatsRows">
            {group.skills.map((skill) => (
              <SkillStatRow
                key={skill.id}
                ordinal={skill.ordinal}
                item={skill}
                accentColor={group.accentColor}
              />
            ))}
          </ul>
        </section>

        <hr className="skillStatsDivider" />

        <footer className="skillStatsFooter">
          <div className="skillStatsOverall">
            <span className="skillStatsOverallTitle">
              <OverallConfidenceArrow color={group.accentColor} />
              {cardText.overallConfidence}
            </span>
            <div className="skillStatsRowRight">
              <span className="skillStatsOverallValue">{overallLabel}</span>
              <ConfidencePills
                value={overallConfidence}
                color={group.accentColor}
                large
              />
            </div>
          </div>

          <div className="skillStatsMetaRow">
            <p className="skillStatsTrend">
              {trendText && (
                <>
                  <span
                    className="skillStatsTrendIcon"
                    aria-hidden="true"
                  >
                    <img
                      src={overallConfidence === 5 ? cardText.trendMaxIcon : cardText.trendUpIcon}
                      alt={
                        overallConfidence === 5 ? cardText.trendMaxIconAlt : cardText.trendUpIconAlt
                      }
                    />
                  </span>
                  {trendText}
                </>
              )}
            </p>

            {group.hasResources && (
              <button
                type="button"
                className="skillStatsButton"
                onClick={() => onCtaClick?.(group)}
              >
                { (screenSize == 'sm' || screenSize =='xs') ? cardText.buttonLabelSm : cardText.buttonLabel}
              </button>
            )}
          </div>
        </footer>
      </div>
    </article>
  );
};
